import { FC } from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { SystemColors } from 'melp-design/style';
import { TransmissionAct } from 'store/transmission-acts';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { formatCurrency } from 'utils/format';
import { APP } from 'config';
import { Currency } from 'types/general';

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Inter',
    lineHeight: 1.4,
    padding: 30,
  },
  fontXS: {
    fontSize: 8,
  },
  fontSM: {
    fontSize: 10,
  },
  fontMD: {
    fontSize: 12,
  },
  fontLG: {
    fontSize: 14,
  },
  fontXL: {
    fontSize: 18,
  },
  fontBold: {
    fontStyle: 'bold',
  },
  flexRow: {
    flexDirection: 'row',
  },
  justifyBetween: {
    justifyContent: 'space-between',
  },
  justifyEnd: {
    justifyContent: 'flex-end',
  },
  pt5: {
    paddingTop: 5,
  },
  pb5: {
    paddingBottom: 5,
  },
  mt5: {
    marginTop: 5,
  },
  mb2: {
    marginBottom: 2,
  },
  mb5: {
    marginBottom: 5,
  },
  mb10: {
    marginBottom: 10,
  },
  mb20: {
    marginBottom: 20,
  },
  mb30: {
    marginBottom: 30,
  },
  textCenter: {
    textAlign: 'center',
  },
  textRight: {
    textAlign: 'right',
  },
  borderTop: {
    borderTopWidth: 1,
    borderTopColor: SystemColors.black,
  },
  'col1/3': {
    width: '33.33%',
  },
  'col1/2': {
    width: '50%',
  },
});

interface Props {
  transmissionAct: TransmissionAct;
  currency?: Currency;
}

export const TransmissionActDocument: FC<Props> = ({
  transmissionAct,
  currency,
}) => {
  const { t, i18n } = useTranslation();

  return (
    <Document>
      <Page size="A4" style={[styles.page, styles.fontSM]}>
        <View style={[styles.textCenter, styles.mb20]}>
          <Text style={[styles.fontXL]}>
            {t('transmissionActs.title', { count: 1 })}
          </Text>
          <Text style={[styles.fontLG, styles.mb5]}>
            {`#${transmissionAct.sequence || transmissionAct.id}`}
          </Text>
          <Text>
            {[
              moment(transmissionAct.from).format('l'),
              moment(transmissionAct.to).format('l'),
            ].join(' - ')}
          </Text>
        </View>

        <View style={[styles.mb30]}>
          <View style={[styles.flexRow]}>
            <View style={[styles['col1/2']]}>
              <Text>MELP, UAB</Text>
              <Text>Laisvės al. 82, Kaunas, LT-44250, Lithuania</Text>
              <Text>Reg. No. 305615566</Text>
              <Text>VAT: LT100013628416</Text>
              <Text>LT297300010171338394, Swedbank AB</Text>
            </View>
            <View style={[styles['col1/2']]}>
              {transmissionAct.billingInfo.split('\n').map((line, i) => (
                <Text key={i}>{line}</Text>
              ))}
            </View>
          </View>
        </View>

        <View style={[styles.mb30]}>
          <Text style={[styles.fontMD, styles.mb10]}>
            {t('employees.reserved')}
          </Text>

          <View style={[styles.flexRow, styles.mb5, styles.fontBold]}>
            <Text style={[styles['col1/3']]}>
              {t('wallet.employeeWalletFundsSource')}
            </Text>
            <Text style={[styles['col1/3'], styles.textCenter]}>
              {t('common.count')}
            </Text>
            <Text style={[styles['col1/3'], styles.textRight]}>
              {t('marketplace.ordersTablePrice')}
            </Text>
          </View>

          {transmissionAct.balance.expenses.transactions.map((expense, i) => {
            return (
              <View style={[styles.flexRow, styles.mb5]} key={i}>
                <Text style={[styles['col1/3']]}>
                  {expense.title === 'reimbursementClaim'
                    ? t('claims.title')
                    : expense.title === 'marketplaceOrder'
                    ? t('menu.orders')
                    : ''}
                </Text>
                <Text style={[styles['col1/3'], styles.textCenter]}>
                  {expense.count}
                </Text>
                <Text style={[styles['col1/3'], styles.textRight]}>
                  {formatCurrency(
                    i18n.language,
                    expense.amount,
                    expense.currency,
                  )}
                </Text>
              </View>
            );
          })}

          <View
            style={[
              styles.flexRow,
              styles.justifyEnd,
              styles.borderTop,
              styles.mt5,
              styles.pt5,
            ]}
          >
            <Text style={[styles.fontBold]}>
              {t('compare_mode.total')}:{' '}
              {formatCurrency(
                i18n.language,
                transmissionAct.balance.expenses.total,
                currency,
              )}
            </Text>
          </View>
        </View>

        <View style={[styles.mb30]}>
          <Text style={[styles.fontMD, styles.mb10]}>
            {t('expenses.filters.type.options.deposit')}
          </Text>

          <View style={[styles.flexRow, styles.mb5, styles.fontBold]}>
            <Text style={[styles['col1/3']]}>{t('common.date')}</Text>
            <Text style={[styles['col1/3'], styles.textCenter]}>
              {t('common.invoice_number.label')}
            </Text>
            <Text style={[styles['col1/3'], styles.textRight]}>
              {t('marketplace.ordersTablePrice')}
            </Text>
          </View>

          {transmissionAct.balance.deposits.transactions.map((deposit, i) => {
            return (
              <View style={[styles.flexRow, styles.mb5]} key={i}>
                <Text style={[styles['col1/3']]}>
                  {moment(deposit.date).format('l')}
                </Text>
                <Text style={[styles['col1/3'], styles.textCenter]}>
                  {deposit.title}
                </Text>
                <Text style={[styles['col1/3'], styles.textRight]}>
                  {formatCurrency(
                    i18n.language,
                    deposit.amount,
                    deposit.currency,
                  )}
                </Text>
              </View>
            );
          })}

          <View
            style={[
              styles.flexRow,
              styles.justifyEnd,
              styles.borderTop,
              styles.mt5,
              styles.pt5,
            ]}
          >
            <Text style={[styles.fontBold]}>
              {t('compare_mode.total')}:{' '}
              {formatCurrency(
                i18n.language,
                transmissionAct.balance.deposits.total,
                currency,
              )}
            </Text>
          </View>
        </View>

        <View style={[styles.mb30]}>
          <View style={[styles.flexRow, styles.justifyBetween, styles.mb5]}>
            <Text>{t('transmissionActs.openingBalance.label')}</Text>
            <Text>
              {formatCurrency(
                i18n.language,
                transmissionAct.openingBalance,
                currency,
              )}
            </Text>
          </View>

          <View style={[styles.flexRow, styles.justifyBetween, styles.mb5]}>
            <Text>{t('employees.reserved')}</Text>
            <Text>
              {formatCurrency(
                i18n.language,
                transmissionAct.balance.expenses.total
                  ? -transmissionAct.balance.expenses.total
                  : 0,
                currency,
              )}
            </Text>
          </View>

          <View style={[styles.flexRow, styles.justifyBetween, styles.mb5]}>
            <Text>{t('expenses.filters.type.options.deposit')}</Text>
            <Text>
              {formatCurrency(
                i18n.language,
                transmissionAct.balance.deposits.total,
                currency,
              )}
            </Text>
          </View>

          <View
            style={[
              styles.flexRow,
              styles.justifyEnd,
              styles.borderTop,
              styles.pt5,
              styles.mt5,
            ]}
          >
            <Text style={[styles.fontBold]}>
              {t('transmissionActs.closingBalance.label')}:{' '}
              {formatCurrency(
                i18n.language,
                transmissionAct.closingBalance,
                currency,
              )}
            </Text>
          </View>
        </View>

        <View style={[styles.mb20]}>
          <Text style={[styles.fontBold]}>
            {t('companies.additional_information')}:
          </Text>
          <Text>
            {transmissionAct.closingBalance < 0
              ? t('transmissionActs.info2.text')
              : t('transmissionActs.info1.text')}
          </Text>
        </View>

        <View style={[styles.mb20]}>
          <Text style={[styles.fontBold]}>{t('common.contacts.label')}:</Text>
          <Text>{APP.email.info}</Text>
        </View>
      </Page>
    </Document>
  );
};
